import { useMemo, useState, useCallback, useEffect } from "react";
import { useClips, useClipHourlyCounts, type ClipItem } from "@visioni/a3s-media/app";
import { type ISODate, type ISODateString } from "@visioni/a3s-codecs";
import { startOfDay, addHours } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import { SiteId, AssetCameraKey } from "@visioni/a3s-schema";

// ============================================================================
// TYPES
// ============================================================================

export type ClipMetadata = {
  id: string;
  timestamp: string;
  duration: number;
  thumbnailUrl: string | null;
  videoUrl: string;
};

export type HourBucket = {
  hour: number; // 0-23
  count: number;
  totalDuration: number;
};

// ============================================================================
// CONSTANTS
// ============================================================================

const CLIPS_PER_HOUR_PAGE = 20;

const EMPTY_HOUR_BUCKETS: readonly HourBucket[] = Object.freeze(
  Array.from({ length: 24 }, (_, hour) => Object.freeze({ hour, count: 0, totalDuration: 0 })),
);

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export function transformToMetadata(item: ClipItem): ClipMetadata {
  return {
    id: item.startTime,
    timestamp: item.startTime,
    duration: item.durationMs,
    thumbnailUrl: item.thumbnailUrl ?? null,
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- fallback for apps deployed before video.url
    videoUrl: item.video?.url ?? item.videoUrl ?? "",
  };
}

/**
 * Compute UTC start/end for a single hour within a date+timezone.
 *
 * `date` must be a pre-zoned Date whose local-time getters already reflect
 * the target timezone (e.g. produced by toZonedTime or from the WeekCalendar).
 *
 * For example, hour 14 on "2025-03-18" in "Australia/Sydney" (UTC+11) →
 *   start: 2025-03-18T03:00:00.000Z  (14:00 AEDT)
 *   end:   2025-03-18T03:59:59.999Z  (14:59:59 AEDT)
 */
function getHourBoundsUtc(
  date: Date,
  hour: number,
  timezone: string,
): { startTime: ISODateString; endTime: ISODateString } {
  const dayStart = startOfDay(date);
  const hourStart = addHours(dayStart, hour);
  const hourEnd = addHours(dayStart, hour + 1);
  hourEnd.setMilliseconds(hourEnd.getMilliseconds() - 1);

  return {
    startTime: fromZonedTime(hourStart, timezone).toISOString() as ISODateString,
    endTime: fromZonedTime(hourEnd, timezone).toISOString() as ISODateString,
  };
}

/**
 * Format a Date's year/month/day components as a YYYY-MM-DD ISODate string.
 * Intended for use on "zoned" dates where getFullYear/getMonth/getDate
 * already reflect the target timezone's calendar date.
 */
function toISODate(date: Date): ISODate {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}` as ISODate;
}

// ============================================================================
// useDayClips — hourly count heatmap for a full day
// ============================================================================

type UseDayClipsParams = {
  siteId: string;
  cameraKey: string;
  selectedDate: Date;
  timezone: string;
  enabled?: boolean;
};

type UseDayClipsReturn = {
  totalCount: number;
  hourBuckets: HourBucket[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
};

export function useDayClipCounts({
  siteId,
  cameraKey,
  selectedDate,
  timezone,
  enabled = true,
}: UseDayClipsParams): UseDayClipsReturn {
  const dateString = useMemo(() => toISODate(startOfDay(selectedDate)), [selectedDate]);

  const parsedSiteId = siteId ? SiteId.fromString(siteId) : undefined;
  const parsedCameraKey = cameraKey ? AssetCameraKey.fromString(cameraKey) : undefined;

  const { data, isLoading, error, refetch } = useClipHourlyCounts({
    ...(parsedSiteId && parsedCameraKey ?
      ({ siteId: parsedSiteId, assetCameraKey: parsedCameraKey } as const)
    : ({ siteId: undefined, assetCameraKey: undefined } as const)),
    date: dateString,
    timezone,
    enabled: enabled && !!siteId && !!cameraKey,
  });

  const hourBuckets: HourBucket[] = useMemo(() => {
    if (!data) return Array.from(EMPTY_HOUR_BUCKETS);
    const buckets = EMPTY_HOUR_BUCKETS.map((b) => ({ ...b }));
    for (const item of data) {
      buckets[item.hour] = { hour: item.hour, count: item.count, totalDuration: item.totalDurationMs };
    }
    return buckets;
  }, [data]);

  return {
    totalCount: data?.reduce((acc, item) => acc + item.count, 0) ?? 0,
    hourBuckets,
    isLoading,
    error: error instanceof Error ? error : null,
    refetch,
  };
}

// ============================================================================
// useHourClips — clips for a single hour, with client-side pagination
// ============================================================================

type UseHourClipsParams = {
  siteId: string;
  cameraKey: string;
  /** Pre-zoned Date that reflects the camera's calendar date */
  selectedDate: Date;
  selectedHour: number;
  timezone: string;
  enabled?: boolean;
};

type UseHourClipsReturn = {
  clips: ClipMetadata[];
  hasMore: boolean;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  loadMore: () => void;
  refetch: () => void;
};

export function useHourClips({
  siteId,
  cameraKey,
  selectedDate,
  selectedHour,
  timezone,
  enabled = true,
}: UseHourClipsParams): UseHourClipsReturn {
  const { startTime, endTime } = useMemo(
    () => getHourBoundsUtc(selectedDate, selectedHour, timezone),
    [selectedDate, selectedHour, timezone],
  );

  const parsedSiteId = siteId ? SiteId.fromString(siteId) : undefined;
  const parsedCameraKey = cameraKey ? AssetCameraKey.fromString(cameraKey) : undefined;

  const {
    data: clipItems,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useClips({
    ...(parsedSiteId && parsedCameraKey ?
      ({ siteId: parsedSiteId, assetCameraKey: parsedCameraKey } as const)
    : ({ siteId: undefined, assetCameraKey: undefined } as const)),
    startTime,
    endTime,
    enabled: enabled && !!siteId && !!cameraKey,
  });

  const allClips = useMemo(() => {
    if (!clipItems) return [];
    return clipItems.map(transformToMetadata);
  }, [clipItems]);

  const [visibleCount, setVisibleCount] = useState(CLIPS_PER_HOUR_PAGE);

  // Reset visible count when hour changes
  useEffect(() => {
    setVisibleCount(CLIPS_PER_HOUR_PAGE);
  }, [selectedHour]);

  const clips = useMemo(() => allClips.slice(0, visibleCount), [allClips, visibleCount]);
  const totalCount = allClips.length;
  const hasMore = visibleCount < totalCount;

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => prev + CLIPS_PER_HOUR_PAGE);
  }, []);

  return {
    clips,
    hasMore,
    isLoading,
    isFetching,
    error: error instanceof Error ? error : null,
    loadMore,
    refetch,
  };
}
